// Session 147j+14 — TriggerReplayChart
// Renders 5-min NIFTY/BANKNIFTY candles + BUY/SELL trigger markers from
// the OI Flow notional trigger (≥3 strikes within ATM window where both
// legs cleared the ₹ Cr threshold AND quadrants align).
//
//   • Green up-triangle (belowBar) = BULL trigger (CE buy + PE write)
//   • Red   down-triangle (aboveBar) = BEAR trigger (CE write + PE buy)

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  createChart, CandlestickSeries, ColorType, CrosshairMode,
  createSeriesMarkers,
} from 'lightweight-charts';
import { Calendar, RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'token';

// Session 147j+15 — IST x-axis fix.
// lightweight-charts v5 hardcodes UTC for axis labels and ignores
// `localization.timeFormatter` / `tickMarkFormatter` for our setup
// (verified: prior fix still showed UTC). Workaround: shift every
// timestamp by +5h30m before feeding the chart. The library then
// renders "shifted-UTC" labels which equal the desired IST values.
// Data integrity stays correct because we apply the same offset to
// both candle times AND marker times — they remain aligned.
const IST_OFFSET_SEC = (5 * 60 + 30) * 60;

const todayIstDate = () => {
  // Build YYYY-MM-DD in IST regardless of viewer locale
  const now = new Date();
  const ist = new Date(now.getTime() + (5 * 60 + 30) * 60 * 1000);
  return ist.toISOString().slice(0, 10);
};

const authHeaders = () => {
  const tok = localStorage.getItem(TOKEN_KEY);
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

const TriggerReplayChart = ({ symbol = 'NIFTY' }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const markersApiRef = useRef(null);

  const [date, setDate] = useState(todayIstDate());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(
        `${API}/api/admin/oi-flow/trigger-events`,
        { params: { symbol, date }, headers: authHeaders() },
      );
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [symbol, date]);

  useEffect(() => { load(); }, [load]);

  // Build / refresh chart whenever data changes
  useEffect(() => {
    if (!containerRef.current) return;

    if (!chartRef.current) {
      chartRef.current = createChart(containerRef.current, {
        width: containerRef.current.clientWidth,
        height: 320,
        layout: {
          background: { type: ColorType.Solid, color: 'transparent' },
          textColor: '#94a3b8',
        },
        grid: {
          vertLines: { color: 'rgba(148, 163, 184, 0.08)' },
          horzLines: { color: 'rgba(148, 163, 184, 0.08)' },
        },
        crosshair: { mode: CrosshairMode.Normal },
        timeScale: {
          borderColor: 'rgba(148, 163, 184, 0.2)',
          timeVisible: true,
          secondsVisible: false,
        },
        rightPriceScale: { borderColor: 'rgba(148, 163, 184, 0.2)' },
      });
      seriesRef.current = chartRef.current.addSeries(CandlestickSeries, {
        upColor: '#10b981',
        downColor: '#ef4444',
        borderUpColor: '#10b981',
        borderDownColor: '#ef4444',
        wickUpColor: '#10b981',
        wickDownColor: '#ef4444',
      });
    }

    // Session 147j+16e — strip off-market candles. The footprint
    // builder isn't gated by market hours, so it can persist 04:00 IST
    // pre-market candles which then dominate the chart's time axis. We
    // keep only candles whose IST minute-of-day is within [09:15, 15:30].
    const inMarketHours = (istTs) => {
      // istTs is already +5.5h shifted; treat it as UTC then read H:M.
      const d = new Date(istTs * 1000);
      const minOfDay = d.getUTCHours() * 60 + d.getUTCMinutes();
      return minOfDay >= 9 * 60 + 15 && minOfDay <= 15 * 60 + 30;
    };

    const candles = (data?.candles || [])
      .filter((c) => c.ts_unix && c.open != null && c.close != null)
      .map((c) => ({
        // Session 147j+15 (re-fix) — backend ts_unix is TRUE UTC seconds
        // (e.g. IST 09:15 → 03:45 UTC). Lightweight-charts v5 displays
        // labels in UTC and ignores tickMarkFormatter for our setup.
        // Manually adding +5.5h makes the rendered labels match IST.
        time: c.ts_unix + IST_OFFSET_SEC,
        open: c.open, high: c.high, low: c.low, close: c.close,
      }))
      .filter((c) => inMarketHours(c.time))
      .sort((a, b) => a.time - b.time);
    seriesRef.current.setData(candles);

    // Markers — bucket each trigger to the matching candle ts.
    // bucket_ts_unix from backend already aligned to the 5m candle open.
    const triggers = (data?.triggers || []).filter((t) => t.bucket_ts_unix);
    const markers = triggers.map((t) => {
      const isBull = t.kind === 'BULL';
      // Session 147j+14 — clearer label: "BUY ×3" (3 strikes matched)
      // instead of "BUY · 3" which read as "BUY -3" / minus three.
      return {
        time: t.bucket_ts_unix + IST_OFFSET_SEC,  // same +5.5h shift as candles
        position: isBull ? 'belowBar' : 'aboveBar',
        color: isBull ? '#10b981' : '#ef4444',
        shape: isBull ? 'arrowUp' : 'arrowDown',
        text: isBull ? `BUY ×${t.match_strikes}` : `SELL ×${t.match_strikes}`,
      };
    })
      .filter((m) => inMarketHours(m.time))
      .sort((a, b) => a.time - b.time);

    if (markersApiRef.current) {
      markersApiRef.current.setMarkers(markers);
    } else if (markers.length > 0) {
      markersApiRef.current = createSeriesMarkers(seriesRef.current, markers);
    }

    if (candles.length > 0) {
      chartRef.current.timeScale().fitContent();
    }
  }, [data]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
      seriesRef.current = null;
      markersApiRef.current = null;
    }
  }, []);

  // Resize on window change
  useEffect(() => {
    const onResize = () => {
      if (chartRef.current && containerRef.current) {
        chartRef.current.applyOptions({ width: containerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const cfg = data?.config || {};
  const triggers = data?.triggers || [];
  const bullCount = triggers.filter((t) => t.kind === 'BULL').length;
  const bearCount = triggers.filter((t) => t.kind === 'BEAR').length;
  const candleCount = (data?.candles || []).length;

  // Empty-state message — distinguish "no candles" vs "candles but no triggers"
  let emptyMsg = null;
  if (!loading && candleCount === 0) {
    emptyMsg = err
      ? `Error: ${err}`
      : `No 5-min ${symbol} candles persisted for ${date}. (Footprint loop hasn't run for this date — try today/yesterday during/after market hours.)`;
  }

  return (
    <div
      className="border border-border bg-surface"
      data-testid={`trigger-replay-chart-${symbol}`}
    >
      <div className="px-3 py-2 border-b border-border flex items-center gap-3 flex-wrap text-[11px] font-mono uppercase tracking-wider">
        <span className="text-foreground font-bold">{symbol} · Trigger Replay · 5m</span>
        <label className="flex items-center gap-1.5 text-muted-foreground normal-case">
          <Calendar className="w-3 h-3" />
          <input
            type="date"
            value={date}
            max={todayIstDate()}
            onChange={(e) => setDate(e.target.value)}
            className="bg-surface-highlight border border-border px-2 py-0.5 text-foreground text-[11px] font-mono"
            data-testid={`trigger-replay-date-${symbol}`}
          />
        </label>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1 px-2 py-0.5 border border-border hover:border-accent hover:text-accent disabled:opacity-50"
          data-testid={`trigger-replay-refresh-${symbol}`}
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        <span className="ml-auto flex items-center gap-3 text-[10px] normal-case">
          <span className="inline-flex items-center gap-1 text-emerald-300">
            <TrendingUp className="w-3 h-3" /> {bullCount} BUY
          </span>
          <span className="inline-flex items-center gap-1 text-rose-300">
            <TrendingDown className="w-3 h-3" /> {bearCount} SELL
          </span>
          <span className="text-muted-foreground">
            ≥{cfg.notional_trigger_strikes ?? 3} strikes · ₹{cfg.min_notional_cr ?? 8} Cr/leg
          </span>
        </span>
      </div>

      <div className="relative">
        <div
          ref={containerRef}
          className="w-full"
          style={{ height: 320 }}
          data-testid={`trigger-replay-canvas-${symbol}`}
        />
        {emptyMsg && (
          <div
            className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground px-6 text-center"
            data-testid={`trigger-replay-empty-${symbol}`}
          >
            {emptyMsg}
          </div>
        )}
      </div>

      {/* Where to find the BUY/SELL signal — answered inline */}
      {candleCount > 0 && triggers.length === 0 && !loading && (
        <div
          className="px-3 py-2 border-t border-border bg-surface-highlight/40 text-[10px] font-mono text-muted-foreground"
          data-testid={`trigger-replay-no-signals-${symbol}`}
        >
          Candles loaded but no notional triggers fired on {date}. Signals
          appear here as <span className="text-emerald-300">▲ green BUY</span>{' '}
          / <span className="text-rose-300">▼ red SELL</span> arrows on the
          chart AND as rows in the table below — only when ≥{cfg.notional_trigger_strikes ?? 3}{' '}
          strikes within the ATM window cleared the ₹{cfg.min_notional_cr ?? 8}{' '}
          Cr threshold on both legs.
        </div>
      )}

      {triggers.length > 0 && (
        <div className="border-t border-border max-h-[200px] overflow-y-auto">
          <table className="w-full text-[11px] font-mono">
            <thead className="sticky top-0 bg-surface-highlight">
              <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-2 py-1.5 text-left">Minute</th>
                <th className="px-2 py-1.5 text-center">Trigger</th>
                <th className="px-2 py-1.5 text-right">Strikes</th>
                <th className="px-2 py-1.5 text-right">CE ₹ Total</th>
                <th className="px-2 py-1.5 text-right">PE ₹ Total</th>
                <th className="px-2 py-1.5 text-left">Bucket</th>
              </tr>
            </thead>
            <tbody>
              {triggers.map((t, i) => {
                const isBull = t.kind === 'BULL';
                return (
                  <tr
                    key={`${t.ist_minute}-${i}`}
                    className="border-b border-border/40"
                    data-testid={`trigger-replay-row-${i}`}
                  >
                    <td className="px-2 py-1 text-foreground">
                      {(t.ist_minute || '').slice(11) || t.ist_minute}
                    </td>
                    <td className="px-2 py-1 text-center">
                      <span
                        className={`inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-wider border ${
                          isBull
                            ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-200'
                            : 'border-rose-500/60 bg-rose-500/10 text-rose-200'
                        }`}
                      >
                        {isBull ? 'BUY (call+ put−)' : 'SELL (call− put+)'}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-right text-foreground">{t.match_strikes}</td>
                    <td className="px-2 py-1 text-right text-foreground">{t.ce_total_cr} Cr</td>
                    <td className="px-2 py-1 text-right text-foreground">{t.pe_total_cr} Cr</td>
                    <td className="px-2 py-1 text-muted-foreground">{t.bucket_time_ist}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TriggerReplayChart;
